import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  ArrowRight, CheckCircle2, AlertTriangle, Loader2,
  FileSpreadsheet, Truck, Columns, Sparkles, Settings2,
  Pencil, Check, X,
} from 'lucide-react';

const FIELD_LABELS = {
  origin: 'Origin',
  destination: 'Destination',
  weight: 'Weight',
  transport_mode: 'Transport Mode',
  vehicle_type: 'Vehicle Type',
  fuel_type: 'Fuel Type',
  good_name: 'Goods Name',
  cost_value: 'Cost',
  ghg_category: 'GHG Category',
};

const REQUIRED_FIELDS = ['origin', 'destination', 'weight', 'transport_mode', 'vehicle_type'];

const UNIT_LABELS = {
  kg: 'Kilograms (kg)',
  tons: 'Metric Tonnes',
  lbs: 'Pounds (lbs)',
  grams: 'Grams',
};

const TRANSPORT_MODES = ['road', 'rail', 'air', 'water'];

const FUEL_OPTIONS = [
  { value: 'diesel', label: 'Diesel' },
  { value: 'petrol', label: 'Petrol' },
  { value: 'cng', label: 'CNG' },
];

export default function MappingPreview({
  filename,
  totalRows,
  columns,
  proposedMapping,
  vehiclePreview,
  detectedDefaults,
  autoReady,
  uploadWarnings,
  isCalculating,
  onCalculate,
  onEditMapping,
  onReset,
  vehicleOptions,
  onVehicleOverride,
  vehicleFuelOverrides,
}) {
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [editMode, setEditMode] = useState('');
  const [editVehicleKey, setEditVehicleKey] = useState('');
  const [editFuelType, setEditFuelType] = useState('diesel');

  // Build mapping entries from proposed mapping
  const mappingEntries = [];
  if (proposedMapping) {
    for (const [field, label] of Object.entries(FIELD_LABELS)) {
      const mapping = proposedMapping[field];
      if (mapping?.column) {
        mappingEntries.push({
          field,
          label,
          column: mapping.column,
          confidence: mapping.confidence,
          required: REQUIRED_FIELDS.includes(field),
        });
      }
    }
  }

  // Calculate/Emissions needs backend-required fields: origin, destination, weight.
  // transport_mode and vehicle_type have safe mode defaults, so they shouldn't
  // block the button — only warn. This matches the /calculate endpoint's real
  // validation (missing origin/dest/weight → row error; missing mode → defaults to "road").
  const hasOrigin = !!proposedMapping?.origin?.column;
  const hasDestination = !!proposedMapping?.destination?.column;
  const hasWeight = !!proposedMapping?.weight?.column;
  const canCalculate = hasOrigin && hasDestination && hasWeight;
  const missingRequiredFields = [];
  if (!hasOrigin) missingRequiredFields.push('Origin');
  if (!hasDestination) missingRequiredFields.push('Destination');
  if (!hasWeight) missingRequiredFields.push('Weight');

  // Get ALL unique vehicle preview entries
  const vehicleEntries = Object.entries(vehiclePreview || {});
  const totalVehicles = vehicleEntries.length;

  // The server reconciles header → AI → kg into proposedMapping.weight_unit; prefer that.
  const weightUnit = proposedMapping?.weight_unit || detectedDefaults?.weight_unit || 'kg';
  const ghgCategory = detectedDefaults?.ghg_category || 'upstream';

  const startEditing = (vehicleText, resolution) => {
    setEditingVehicle(vehicleText);
    setEditMode(resolution.transport_mode || 'road');
    setEditVehicleKey(resolution.resolved_key || '');
    setEditFuelType(vehicleFuelOverrides?.[vehicleText.toLowerCase()] || 'diesel');
  };

  const confirmEdit = () => {
    if (editingVehicle && editVehicleKey) {
      const opt = (vehicleOptions?.[editMode] || []).find(o => o.key === editVehicleKey);
      onVehicleOverride(
        editingVehicle,
        editVehicleKey,
        opt?.display_name || editVehicleKey.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
        editFuelType,
      );
    }
    setEditingVehicle(null);
  };

  const getTierBadge = (tier) => {
    const styles = {
      0: 'border-purple-300 text-purple-700',
      1: 'border-green-300 text-green-700',
      2: 'border-blue-300 text-blue-700',
    };
    const labels = { 0: 'Manual', 1: 'Alias', 2: 'AI' };
    return {
      className: styles[tier] || 'border-gray-300 text-gray-600',
      label: labels[tier] || 'Default',
    };
  };

  return (
    <div className="space-y-4 max-w-2xl mx-auto">
      {/* Header Card */}
      <Card className="pro-card">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
              <FileSpreadsheet className="w-6 h-6 text-green-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-gray-800">
                Ready to Calculate
              </h3>
              <p className="text-sm text-gray-500 mt-0.5">
                <span className="font-medium text-gray-700">{filename}</span>
                {' '}&mdash; {totalRows} rows, {columns.length} columns
              </p>
              {autoReady && (
                <div className="flex items-center gap-1.5 mt-2">
                  <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                  <span className="text-xs font-medium text-amber-700">
                    AI has mapped all required columns with high confidence
                  </span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Warnings */}
      {uploadWarnings?.length > 0 && (
        <div className="space-y-2">
          {uploadWarnings.map((w, i) => (
            <div key={i} className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-500" />
              <span>{w}</span>
            </div>
          ))}
        </div>
      )}

      {/* Column Mapping Summary */}
      <Card className="pro-card">
        <CardHeader className="pb-2 bg-gradient-to-r from-sky-50 to-white border-b border-sky-100">
          <CardTitle className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <Columns className="w-4 h-4 text-sky-600" />
            AI Column Mapping
          </CardTitle>
        </CardHeader>
        <CardContent className="p-3">
          {mappingEntries.length > 0 ? (
            <div className="space-y-1.5">
              {mappingEntries.map(({ field, label, column, confidence, required }) => (
                <div key={field} className="flex items-center gap-2 text-sm py-1 px-2 rounded bg-gray-50">
                  <span className="w-28 flex-shrink-0 font-medium text-gray-600 flex items-center gap-1.5">
                    {label}
                    {required && (
                      <span className="text-[9px] px-1 py-0 bg-red-100 text-red-600 rounded font-semibold">REQ</span>
                    )}
                  </span>
                  <ArrowRight className="w-3 h-3 text-gray-300 flex-shrink-0" />
                  <span className="flex-1 text-gray-800 font-medium truncate">{column}</span>
                  <span className={`text-xs font-medium flex-shrink-0 ${
                    confidence >= 0.8 ? 'text-green-600' : confidence >= 0.5 ? 'text-amber-600' : 'text-red-600'
                  }`}>
                    {Math.round(confidence * 100)}%
                  </span>
                  {confidence >= 0.8 && <CheckCircle2 className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 py-2">
              AI could not map columns. Click "Edit Mapping" to map manually.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Vehicle Resolution — scrollable, editable */}
      {totalVehicles > 0 && (
        <Card className="pro-card">
          <CardHeader className="pb-2 bg-gradient-to-r from-violet-50 to-white border-b border-violet-100">
            <CardTitle className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <Truck className="w-4 h-4 text-violet-600" />
              Vehicle Resolution
              <Badge variant="outline" className="text-[10px] ml-auto">
                {totalVehicles} unique
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3">
            <div className="max-h-72 overflow-y-auto space-y-1.5">
              {vehicleEntries.map(([vehicleText, resolution]) => {
                const badge = getTierBadge(resolution.tier);
                const isEditing = editingVehicle === vehicleText;

                return (
                  <div key={vehicleText} className="py-1.5 px-2 rounded bg-gray-50">
                    {/* Vehicle row */}
                    <div className="flex items-center gap-2 text-sm">
                      <span className="flex-1 text-gray-700 capitalize min-w-0 truncate" title={vehicleText}>
                        {vehicleText}
                      </span>
                      <ArrowRight className="w-3 h-3 text-gray-300 flex-shrink-0" />
                      <span className="text-gray-800 font-medium text-xs truncate max-w-[200px]" title={resolution.resolved_key}>
                        {resolution.display_name || resolution.resolved_key}
                      </span>
                      <Badge variant="outline" className={`text-[9px] flex-shrink-0 ${badge.className}`}>
                        {badge.label}
                      </Badge>
                      {vehicleOptions && (
                        <button
                          onClick={() => isEditing ? setEditingVehicle(null) : startEditing(vehicleText, resolution)}
                          className="p-1 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600 flex-shrink-0"
                          title="Edit vehicle mapping"
                        >
                          <Pencil className="w-3 h-3" />
                        </button>
                      )}
                    </div>

                    {/* Inline edit row */}
                    {isEditing && vehicleOptions && (
                      <div className="mt-2 flex items-center gap-2 border-t border-gray-200 pt-2">
                        <Select value={editMode} onValueChange={(mode) => {
                          setEditMode(mode);
                          const modeOpts = vehicleOptions[mode] || [];
                          setEditVehicleKey(modeOpts[0]?.key || '');
                        }}>
                          <SelectTrigger className="w-24 h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {TRANSPORT_MODES.map(m => (
                              <SelectItem key={m} value={m} className="text-xs capitalize">{m}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        <Select value={editVehicleKey} onValueChange={setEditVehicleKey}>
                          <SelectTrigger className="flex-1 h-8 text-xs">
                            <SelectValue placeholder="Select vehicle type..." />
                          </SelectTrigger>
                          <SelectContent>
                            {(vehicleOptions[editMode] || []).map(opt => (
                              <SelectItem key={opt.key} value={opt.key} className="text-xs">
                                {opt.display_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        <Select value={editFuelType} onValueChange={setEditFuelType}>
                          <SelectTrigger className="w-24 h-8 text-xs" title="Fuel type">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {FUEL_OPTIONS.map(f => (
                              <SelectItem key={f.value} value={f.value} className="text-xs">{f.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        <button
                          onClick={confirmEdit}
                          className="p-1.5 rounded bg-green-100 text-green-700 hover:bg-green-200 flex-shrink-0"
                          title="Confirm"
                        >
                          <Check className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setEditingVehicle(null)}
                          className="p-1.5 rounded bg-gray-100 text-gray-500 hover:bg-gray-200 flex-shrink-0"
                          title="Cancel"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Detected Defaults */}
      <Card className="pro-card">
        <CardHeader className="pb-2 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
          <CardTitle className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <Settings2 className="w-4 h-4 text-gray-500" />
            Detected Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="p-3">
          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-1.5 text-sm">
              <span className="text-gray-500">Weight Unit:</span>
              <Badge variant="secondary" className="text-xs">{UNIT_LABELS[weightUnit] || weightUnit}</Badge>
            </div>
            <div className="flex items-center gap-1.5 text-sm">
              <span className="text-gray-500">GHG Category:</span>
              <Badge variant="secondary" className="text-xs capitalize">{ghgCategory}</Badge>
            </div>
            <div className="flex items-center gap-1.5 text-sm">
              <span className="text-gray-500">Default Fuel:</span>
              <Badge variant="secondary" className="text-xs">Diesel</Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-2">
        <Button variant="outline" onClick={onReset} disabled={isCalculating}>
          Upload Different File
        </Button>
        <div className="flex flex-col items-end gap-2">
          {!canCalculate && (
            <span className="flex items-center gap-1 text-xs text-red-600">
              <AlertTriangle className="w-3 h-3" />
              Map required fields: {missingRequiredFields.join(', ')}
            </span>
          )}
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={onEditMapping} disabled={isCalculating}>
              Edit Mapping
            </Button>
            <Button
              onClick={onCalculate}
              disabled={isCalculating || !canCalculate}
              className="bg-sky-600 hover:bg-sky-700"
            >
              {isCalculating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Starting...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Calculate Emissions
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
